import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';
import {Link} from 'react-router-dom';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AfriexAdminTransaction} from '@/types';
import {AfriexTransactionType, statusColors} from '@/types/Transaction';
import {
  capitalizeWords,
  formatTxChannel,
  getTxAmountAndAsset,
} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import TransactionSummary from './TransactionSummary';

const TransactionTable = ({items}: {items: AfriexAdminTransaction[]}) => {
  return (
    <Table className="overflow-hidden" data-cy="admin-transactions-table">
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th>Amount</Th>
          <Th>Asset</Th>
          <Th className="w-auto">Status</Th>
          <Th>Type</Th>
          <Th>Channel</Th>
          <Th>Date Initiated</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>
      <TBody data-cy="admin-transactions-table-body">
        {items.length === 0 ? (
          <Tr enableHover={false}>
            <Td
              colSpan={8}
              className="text-center py-6 text-gray-500"
              data-cy="admin-transactions-empty-state"
            >
              No transactions found
            </Td>
          </Tr>
        ) : (
          items.map((item, index) => (
            <TRow key={index} item={item} index={index} />
          ))
        )}
      </TBody>
    </Table>
  );
};

const TRow = ({item, index}: {item: AfriexAdminTransaction; index: number}) => {
  const transactionStatus = item.status;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const {
    txType,
    initiatedAmount,
    initiatedAsset,
    fulfilledAmount,
    fulfilledAsset,
  } = getTxAmountAndAsset(item, 'rate');

  // Payouts (WITHDRAW and SEND) move money out to a recipient, so transaction
  // monitoring needs the destination (fulfilled) currency, not the source.
  const isPayout =
    txType === AfriexTransactionType.WITHDRAW ||
    txType === AfriexTransactionType.SEND;
  const displayAmount = isPayout ? fulfilledAmount : initiatedAmount;
  const displayAsset = isPayout ? fulfilledAsset : initiatedAsset;
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  const redirectLink = `/transactions/${item.id}`;

  return (
    <>
      <Tr
        className={zebraRow}
        data-cy="admin-transactions-row"
        data-transaction-id={item.id}
      >
        <Td isNumeric>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            data-cy="admin-transactions-row-expand"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              ) : (
                <PlusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              )}
            </div>
          </Button>
        </Td>

        <Td className={`font-medium`} data-cy="admin-transactions-row-amount">
          {displayAmount}
        </Td>

        <Td data-cy="admin-transactions-row-asset">{displayAsset}</Td>

        <Td data-cy="admin-transactions-row-status">
          <Badge
            label={transactionStatus}
            colorScheme={statusColors[transactionStatus]}
          />
        </Td>

        <Td className={`capitalize`} data-cy="admin-transactions-row-type">
          {capitalizeWords(item.type?.replace(/_/g, ' '))}
        </Td>

        <Td className={`capitalize`} data-cy="admin-transactions-row-channel">
          {formatTxChannel(item)}
        </Td>

        <Td data-cy="admin-transactions-row-date">
          {formatDate.DDMonYYYY(item.createdAt?.toString() || '')}
        </Td>

        <Td>
          <Link
            to={redirectLink}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="admin-transactions-row-details-link"
          >
            <div className="w-5 h-5 flex justify-center text-gray-700 dark:text-gray-300 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500">
              <ChevronRightIcon className="w-5 h-5" />
            </div>
          </Link>
        </Td>
      </Tr>

      {isExpanded && (
        // `data-transaction-id` is what ties the panel to its row: the expanded
        // <Tr> is a sibling, not a child, so without it a test can only reach
        // it by DOM position. It deliberately does NOT carry
        // `admin-transactions-row`, so row-count assertions stay unaffected.
        <Tr
          enableHover={false}
          data-cy="admin-transactions-row-summary"
          data-transaction-id={item.id}
        >
          <Td colSpan={9} className="p-0 m-0 whitespace-nowrap">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <TransactionSummary data={item} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default TransactionTable;
